<template>
  <button @click="isOpen = true">
    <BaseAvatar :icon="['fal', 'users']" hide-status :size="avatarSize" />
  </button>
  <Dialog :open="isOpen" class="relative z-100" @close="isOpen = false">
    <div class="fixed inset-0 bg-black/10" aria-hidden="true" />
    <div class="fixed inset-0 flex w-screen items-center justify-center p-4">
      <DialogPanel
        class="relative flex h-[80vh] w-full max-w-xl flex-col gap-4 overflow-hidden rounded bg-white pt-6"
      >
        <button
          class="absolute top-4 right-4 active:outline-none"
          @click="isOpen = false"
        >
          <FontAwesomeIcon :icon="['fal', 'times']" size="lg" />
        </button>
        <DialogTitle
          class="shrink-0 px-6 text-xl leading-none font-bold capitalize"
        >
          {{ $t('global.planning') }}
        </DialogTitle>
        <div class="flex flex-row gap-4 border-b px-6">
          <button
            class="flex items-center gap-2 border-b pb-1"
            :class="{
              'border-primary': view === 'users',
              'border-transparent': view === 'material_resources'
            }"
            @click="view = 'users'"
          >
            <div class="capitalize-first font-bold">
              {{ $t('attributes.users') }}
            </div>
            {{ selectedUsers?.length }}
          </button>
          <button
            v-if="
              !hideMeterialResources && Array.isArray(selectedMaterialResources)
            "
            class="flex items-center gap-2 border-b pb-1"
            :class="{
              'border-primary': view === 'material_resources',
              'border-transparent': view === 'users'
            }"
            @click="view = 'material_resources'"
          >
            <div class="capitalize-first font-bold">
              {{ $t('attributes.material_resources') }}
              {{ selectedMaterialResources?.length }}
            </div>
          </button>
        </div>
        <UsersPanel
          v-if="view === 'users'"
          :selected-users="selectedUsers"
          :filter="filter"
          :edition-mode="editionMode"
          @add-user="(user_id) => $emit('add-user', user_id)"
        >
          <template #selected-user-panel="{ user }">
            <slot name="selected-user-panel" :user="user" />
          </template>
          <template #selectable-user-panel="{ user }">
            <slot name="selectable-user-panel" :user="user" />
          </template>
          <template #top-left-cta>
            <slot name="top-left-cta" />
          </template>
        </UsersPanel>
        <MaterialResourcesPanel
          v-else-if="view === 'material_resources'"
          :selected-material-resources="selectedMaterialResources"
          :edition-mode="editionMode"
          :event="event"
          :all-users-availability-items="allUsersAvailabilityItems"
          :all-material-resources-availability-items="
            allMaterialResourcesAvailabilityItems
          "
          @add-material-resource="
            (material_resource_id) =>
              $emit('add-material-resource', material_resource_id)
          "
          @remove-material-resource="
            (material_resource_id) =>
              $emit('remove-material-resource', material_resource_id)
          "
        />
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import {
  Dialog,
  DialogDescription,
  DialogPanel,
  DialogTitle
} from '@headlessui/vue'
import BaseAvatar from '@c/Avatar/BaseAvatar.vue'
import UserAvatar from '@c/Avatar/UserAvatar.vue'
import BaseSearchInput from '@c/addf-package/components/BaseInput/BaseSearchInput.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import UsersPanel from '@c/BaseSelectorComponent/UserSelectorModal/UsersPanel.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import MaterialResourcesPanel from '@c/BaseSelectorComponent/UserSelectorModal/MaterialResourcesPanel.vue'

export default defineComponent({
  name: 'UserSelectorModal',
  components: {
    MaterialResourcesPanel,
    FontAwesomeIcon,
    UsersPanel,
    SelectComponent,
    BaseSearchInput,
    UserAvatar,
    BaseAvatar,
    DialogDescription,
    DialogTitle,
    DialogPanel,
    Dialog
  },
  props: {
    event: {
      type: Object,
      required: false,
      default: () => ({})
    },
    selectedUsers: {
      type: Array,
      required: true
    },
    selectedMaterialResources: {
      type: Array,
      required: false,
      default: null
    },
    filter: {
      type: Function,
      required: false,
      default: undefined
    },
    editionMode: {
      type: Boolean,
      required: false,
      default: false
    },
    hideMeterialResources: {
      type: Boolean,
      required: false,
      default: false
    },
    avatarSize: {
      type: String,
      required: false,
      default: 'small'
    },
    allUsersAvailabilityItems: {
      type: Array,
      required: false,
      default: () => []
    },
    allMaterialResourcesAvailabilityItems: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: [
    'modal-open',
    'modal-close',
    'add-user',
    'add-material-resource',
    'remove-material-resource'
  ],
  data() {
    return {
      isOpen: false,
      view: 'users'
    }
  },
  watch: {
    isOpen(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit(newValue ? 'modal-open' : 'modal-close')
      }
    }
  },
  methods: {
    close() {
      this.isOpen = false
    }
  }
})
</script>
